import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, Loader2, Lock, Package, Users, CreditCard, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "@/lib/formatters";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { SubscriptionModal } from "@/components/payment/SubscriptionModal";
import { ExtensionPaymentModal } from "@/components/payment/ExtensionPaymentModal";
import * as LucideIcons from "lucide-react";
import PilatesPlannerExtension from "@/pages/extensions/pilatesplanner";
import MealPlanExtension from "@/pages/extensions/mealplan";
import PrescriptionList from "@/pages/extensions/prescription/PrescriptionList";
import ClinicalRecordList from "@/pages/extensions/clinicalrecord/ClinicalRecordList";

type Extension = {
  id: string;
  title: string;
  slug: string;
  short_description: string;
  full_description: string | null;
  source_path: string;
  pricing_type: string;
  price_cents: number;
  is_suspended: boolean;
  suspension_message: string | null;
  icon_name: string;
};

// Mapeamento de extensões para componentes
const extensionComponents: Record<string, React.ComponentType> = {
  "extensions/classplan": PilatesPlannerExtension,
  "extensions/meanplan": MealPlanExtension,
  "extensions/prescription": PrescriptionList,
  "extensions/clinicalrecord": ClinicalRecordList,
};

export default function AplicativoView() {
  const { slug } = useParams<{ slug: string }>();
  const [extension, setExtension] = useState<Extension | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [hasAcquired, setHasAcquired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acquisitionCount, setAcquisitionCount] = useState<number>(0);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showExtensionPaymentModal, setShowExtensionPaymentModal] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<{ code: string; name: string; price: number } | null>(null);

  useEffect(() => {
    checkAccess();
  }, [slug]);

  // Log de visualização quando o usuário acessa um app já adquirido
  useEffect(() => {
    if (!loading && hasAcquired && extension) {
      const logView = async () => {
        try {
          // Verificar se o usuário está autenticado antes de fazer a chamada
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          await supabase.functions.invoke('log-user-access', {
            body: {
              action: 'view_extension',
              entity: 'extension',
              entityId: extension.id
            }
          });
        } catch (error) {
          console.error('Error logging extension view:', error);
        }
      };
      logView();
    }
  }, [loading, hasAcquired, extension]);

  const checkAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError("Usuário não autenticado");
        return;
      }

      // Get extension
      const { data: ext, error: extError } = await supabase
        .from("extensions")
        .select("*")
        .eq("slug", slug)
        .single();

      if (extError || !ext) {
        setError("Aplicativo não encontrado");
        return;
      }

      setExtension(ext);

      // Check if suspended
      if (ext.is_suspended) {
        setLoading(false);
        return;
      }

      // Get user's health area and subscription status
      const { data: profile } = await supabase
        .from("profiles")
        .select("health_area_id, health_area, subscription_status, current_plan_code")
        .eq("id", user.id)
        .single();

      let healthAreaId = profile?.health_area_id;

      // Fallback para dados antigos
      if (!healthAreaId && profile?.health_area) {
        const normalizeArea = (area: string) =>
          area.toLowerCase()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-z]/g, '');

        const { data: allAreas } = await supabase
          .from("health_areas")
          .select("id, name")
          .eq("is_active", true);

        const normalizedUserArea = normalizeArea(profile.health_area);
        const matched = allAreas?.find(ha =>
          normalizeArea(ha.name) === normalizedUserArea
        );
        healthAreaId = matched?.id;
      }

      if (!healthAreaId) {
        setError("Área de saúde não encontrada no perfil");
        setLoading(false);
        return;
      }

      // Check if user's health area has access to this extension
      const { data: accessData } = await supabase
        .from("extension_health_areas")
        .select("id")
        .eq("extension_id", ext.id)
        .eq("health_area_id", healthAreaId)
        .single();

      if (!accessData) {
        setError("Este aplicativo não está disponível para sua área de saúde");
        setLoading(false);
        return;
      }

      setHasAccess(true);

      // Check subscription status
      const userIsSubscriber = profile?.subscription_status !== 'free' && profile?.current_plan_code !== 'free';
      setIsSubscriber(userIsSubscriber);

      // Get current plan if subscriber
      if (userIsSubscriber && profile?.current_plan_code) {
        const { data: planData } = await supabase
          .from("subscription_plans")
          .select("code, name, price_cents")
          .eq("code", profile.current_plan_code)
          .single();

        if (planData) {
          setCurrentPlan({
            code: planData.code,
            name: planData.name,
            price: planData.price_cents
          });
        }
      }

      // Check if user has acquired
      const { data: acquisition } = await supabase
        .from("user_extensions")
        .select("id, status")
        .eq("user_id", user.id)
        .eq("extension_id", ext.id)
        .eq("status", "active")
        .single();

      setHasAcquired(!!acquisition);

      // Get acquisition count
      const { count } = await supabase
        .from("user_extensions")
        .select("*", { count: 'exact', head: true })
        .eq("extension_id", ext.id)
        .eq("status", "active");

      setAcquisitionCount(count || 0);

    } catch (error: any) {
      toast.error("Erro ao verificar acesso: " + error.message);
      setError("Erro ao carregar aplicativo");
    } finally {
      setLoading(false);
    }
  };

  const handleAcquire = async () => {
    if (!extension) return;

    try {
      // Se gratuito, insere direto
      if (extension.pricing_type === "free") {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { error } = await supabase
          .from("user_extensions")
          .insert({
            user_id: user.id,
            extension_id: extension.id,
            status: "active",
          });

        if (error) throw error;

        toast.success("Aplicativo adquirido com sucesso!");
        setHasAcquired(true);

        // Log de acesso (reutilizando user da verificação acima)
        if (user) {
          await supabase.functions.invoke('log-user-access', {
            body: {
              action: 'acquire_extension',
              entity: 'extension',
              entityId: extension.id
            }
          });
        }

        return;
      }

      // Se pago, verificar se é assinante
      if (!isSubscriber) {
        setShowSubscriptionModal(true);
        return;
      }

      // Se já é assinante, abrir wizard de pagamento
      setShowExtensionPaymentModal(true);

    } catch (error: any) {
      toast.error("Erro ao adquirir aplicativo: " + error.message);
    }
  };

  const handleExtensionPaymentSuccess = async () => {
    setShowExtensionPaymentModal(false);
    setHasAcquired(true);
    toast.success("Aplicativo contratado com sucesso!");

    // Log de acesso
    if (extension) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.functions.invoke('log-user-access', {
          body: {
            action: 'acquire_extension',
            entity: 'extension',
            entityId: extension.id
          }
        });
      }
    }

    // Recarregar dados
    checkAccess();
  };

  const handleSubscriptionSuccess = () => {
    setShowSubscriptionModal(false);
    toast.success("Assinatura realizada! Agora você pode contratar o aplicativo.");
    // Recarregar para atualizar status de assinatura
    checkAccess();
  };

  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName] || Package;
    return Icon;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !extension) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Erro</AlertTitle>
        <AlertDescription>{error || "Aplicativo não encontrado"}</AlertDescription>
      </Alert>
    );
  }

  // Show suspension message
  if (extension.is_suspended) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Aplicativo Suspenso</AlertTitle>
        <AlertDescription>
          {extension.suspension_message || "Este aplicativo está temporariamente suspenso."}
        </AlertDescription>
      </Alert>
    );
  }

  // Show access denied
  if (!hasAccess) {
    return (
      <Alert variant="destructive">
        <Lock className="h-4 w-4" />
        <AlertTitle>Acesso Negado</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  // Show acquisition screen
  if (!hasAcquired) {
    const Icon = getIcon(extension.icon_name);

    return (
      <>
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header com ícone e título */}
          <div className="flex items-start gap-6">
            <div className="p-4 bg-primary/10 rounded-2xl">
              <Icon className="h-12 w-12 text-primary" />
            </div>
            <div className="flex-1">
              <h1 className="text-4xl font-bold tracking-tight mb-2">{extension.title}</h1>
              <p className="text-lg text-muted-foreground">{extension.short_description}</p>
            </div>
          </div>

          <Separator />

          {/* Descrição completa */}
          {extension.full_description && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  Sobre o Aplicativo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                  {extension.full_description}
                </p>
              </CardContent>
            </Card>
          )}

          <Separator />

          {/* Preço e informações */}
          <Card>
            <CardContent className="pt-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Valor</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-4xl font-bold text-primary">
                      {extension.pricing_type === "free" ? "Gratuito" : formatPrice(extension.price_cents)}
                    </p>
                    {extension.pricing_type !== "free" && (
                      <span className="text-muted-foreground">
                        /{extension.pricing_type === "one_time" ? "único" : "mês"}
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-sm text-muted-foreground mb-1">Profissionais</p>
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-muted-foreground" />
                    <p className="text-2xl font-bold">{acquisitionCount}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">já contrataram</p>
                </div>
              </div>

              <Separator />

              {/* Botão de contratação */}
              <div className="space-y-3">
                <Button
                  onClick={handleAcquire}
                  size="lg"
                  className="w-full"
                >
                  {extension.pricing_type === "free" ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 mr-2" />
                      Obter Gratuitamente
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5 mr-2" />
                      Contratar Aplicativo
                    </>
                  )}
                </Button>

                {extension.pricing_type !== "free" && !isSubscriber && (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      Este aplicativo requer uma assinatura ativa. Ao clicar em "Contratar", você será direcionado para escolher um plano.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Modal de assinatura */}
        {currentPlan && (
          <SubscriptionModal
            open={showSubscriptionModal}
            onOpenChange={setShowSubscriptionModal}
            planCode={currentPlan.code}
            planName={currentPlan.name}
            planPrice={currentPlan.price}
            onSuccess={handleSubscriptionSuccess}
          />
        )}

        {/* Modal de pagamento do app */}
        <ExtensionPaymentModal
          open={showExtensionPaymentModal}
          onOpenChange={setShowExtensionPaymentModal}
          extension={extension}
          onSuccess={handleExtensionPaymentSuccess}
        />
      </>
    );
  }

  // Render extension component
  const ExtensionComponent = extensionComponents[extension.source_path];

  if (ExtensionComponent) {
    return <ExtensionComponent />;
  }

  // Fallback para extensões não implementadas
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{extension.title}</h1>
        <p className="text-muted-foreground">{extension.short_description}</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Em Desenvolvimento</AlertTitle>
            <AlertDescription>
              O módulo do aplicativo será carregado de: <code>{extension.source_path}</code>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}
